import React from 'react';
import * as Inovx from '@shared';
import ConfigurationActions from '../actions/ConfigurationActions';
import OntologyActions from '../actions/OntologyActions';
import FacilityActions from '../actions/FacilityActions';
import FacilityStore from '../stores/FacilityStore';
import SchemaActions from '../actions/SchemaActions';
import AssemblyLibraryActions from '../actions/AssemblyLibraryActions';
import AssemblyLibraryStore from '../stores/AssemblyLibraryStore';
import TaxonomyActions from '../actions/TaxonomyActions';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var PermissionService = Inovx.Shared.Permission.PermissionService;
var MANAGE_FACILITY_PERMISSION = "Facility.Manage";
var hasManagePermission: boolean = PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);

declare var InovxLoader: any;

interface ManageSidebarProps { }

interface ManageSidebarStoreSlice {
    sidebarOpen?: boolean;
    configurationModalVisible?: boolean;
    taxonomyModalVisible?: boolean;
    assemblyLibraryVisible?: boolean;
    selectedFacility?: IFacilityDetail;
    ontologyModalVisible?: boolean;
    modalVisible?: boolean;
    dataSourcesVisible?: boolean;
}

function readStore(): ManageSidebarStoreSlice {
    return {
        sidebarOpen: FacilityStore.getSidebarOpen(),
        configurationModalVisible: FacilityStore.getConfigurationModalVisible(),
        taxonomyModalVisible: FacilityStore.getTaxonomyModalVisible(),
        assemblyLibraryVisible: AssemblyLibraryStore.getAssemblyLibraryVisible(),
        selectedFacility: FacilityStore.getSelectedFacility(),
        ontologyModalVisible: FacilityStore.getOntologyModalVisible(),
        modalVisible: FacilityStore.getModalVisible(),
        dataSourcesVisible: FacilityStore.getDataSourcesVisible(),
    };
}

const ManageSidebar: React.FC<ManageSidebarProps> = () => {
    const [storeState, setStoreState] = React.useState<ManageSidebarStoreSlice>(() => readStore());
    const [expandedMenu, setExpandedMenu] = React.useState<string>('');
    const querySearchRef = React.useRef<string>(''); // not rendered; keeps last query string

    // Subscribe to stores
    React.useEffect(() => {
        const handler = () => setStoreState(readStore());
        FacilityStore.addChangeListener(handler);
        AssemblyLibraryStore.addChangeListener(handler);
        return () => {
            FacilityStore.removeChangeListener(handler);
            AssemblyLibraryStore.removeChangeListener(handler);
        };
    }, []);

    // On mount: check expand flag
    React.useEffect(() => {
        const currentUrl = window.location.href;
        const urlObject = new URL(currentUrl);
        const searchParams = new URLSearchParams(urlObject.search);
        const isExpand = searchParams.get('expand');
        if (isExpand === 'true') {
            setTimeout(() => {
                FacilityActions.openSidebar();
            }, 0);
            querySearchRef.current = '?expand=true';
            toggleMainContainerClass('view-expanded', 'view-collapsed');
        }
    }, []);

    const toggleMainContainerClass = (addClass = '', removeClass = '') => {
        const elements = document.getElementsByClassName('facility-app-container');
        for (let i = 0; i < elements.length; i++) {
            elements[i].classList.add(addClass);
            elements[i].classList.remove(removeClass);
        }
    };

    const toggleSidebar = () => {
        if (storeState.sidebarOpen) {
            toggleMainContainerClass('view-collapsed', 'view-expanded');
            FacilityActions.closeSidebar();
            querySearchRef.current = FacilityActions.updateUrlWithExpanded('false');
        } else {
            toggleMainContainerClass('view-expanded', 'view-collapsed');
            FacilityActions.openSidebar();
            querySearchRef.current = FacilityActions.updateUrlWithExpanded('true');
        }
    };

    const toggleSubmenu = () => setExpandedMenu(v => v === 'edit' ? '' : 'edit');

    const isUnsavedForms = () => {
        if (!FacilityActions.verifyAndFinishFacilityModalChanges(true)) {
            return true;
        }
        if (!OntologyActions.verifyAndCloseOntologyModalChanges()) {
            return true;
        }
        if (!SchemaActions.verifyAndCloseConfigurationEdit()) {
            return true;
        }
        if (!AssemblyLibraryActions.verifyAndCloseAssemblyTemplateEdit()) {
            return true;
        }
        if (!TaxonomyActions.verifyAndCloseTaxonomyEdit()) {
            return true;
        }
        return false;
    };

    const openEditFacility = async (tab = '', mode = 'edit') => {
        if (isUnsavedForms()) return;
        FacilityActions.selectTab(tab);
        if (tab === 'integrations' && storeState.selectedFacility) {
            FacilityActions.getFacilityAdapters(storeState.selectedFacility.ID);
        }
        if (mode === 'add') {
            FacilityActions.beginAdd();
        } else if (storeState.selectedFacility) {
            FacilityActions.beginEdit(storeState.selectedFacility);
        }
    };

    const openEditOntology = async () => {
        if (isUnsavedForms()) return;
        OntologyActions.beginEditOntology();
    };

    const openConfiguration = () => {
        if (isUnsavedForms()) return;
        ConfigurationActions.openConfiguration(null);
    };

    const openAssemblyLibrary = () => {
        if (isUnsavedForms()) return;
        AssemblyLibraryActions.openAssemblyLibrary();
    };

    const toggleTaxonomy = () => {
        if (isUnsavedForms()) return;
        TaxonomyActions.toggleTaxonomy(true);
    };

    const openAllFacilities = () => {
        if (isUnsavedForms()) return;
        FacilityActions.openAllFacilities();
    };

    const openDataSources = () => {
        if (isUnsavedForms()) return;
        FacilityActions.openDataSources();
    };

    const sidebarOpen = !!storeState.sidebarOpen;
    const facilityIsSelected: boolean = !!storeState.selectedFacility;
    const selectedMenu = storeState.modalVisible ? FacilityStore.getSelectedTab()
        : storeState.ontologyModalVisible ? 'ontology'
            : storeState.dataSourcesVisible ? 'dataSources'
            : storeState.configurationModalVisible ? 'configuration'
                : storeState.assemblyLibraryVisible ? 'configuration'
                    : storeState.taxonomyModalVisible ? 'configuration'
                        : 'manage';

    return (
        <div id="sidebar" className={`sidebar ${sidebarOpen ? 'show' : ''}`}>
            <nav className="nav">
                <ul className="list-unstyled components">
                    <li>
                        <a className="d-flex j-csb" onClick={() => toggleSidebar()}>
                            <div className="icon">
                                <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/menu_icon.png`} />
                            </div>
                        </a>
                    </li>
                    <li>
                        <a href={`/Home${sidebarOpen ? '?expand=true' : ''}`} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.home')}>
                            <div className="icon">
                                <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/home_icon.png`} />
                            </div>
                            <div className="nav_name">
                                {InovxLoader.t('facility:menu.home')}
                            </div>
                        </a>
                    </li>
                    <li className={`${selectedMenu === 'manage' ? 'active' : ''}`}>
                        <a onClick={() => openAllFacilities()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.manageFacilities')}>
                            <div className="icon">
                                <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/manage_facilities.png`} />
                            </div>
                            <div className="nav_name">
                                {InovxLoader.t('facility:menu.manageFacilities')}
                            </div>
                        </a>
                    </li>
                    <li><hr style={{ width: '90%', margin: '8px auto' }} /></li>
                    {facilityIsSelected ? (<>
                        <li className={`${selectedMenu === 'ontology' ? 'active' : ''}`}>
                            <a className="v-tooltip" onClick={() => openEditOntology()} aria-label={InovxLoader.t('facility:menu.ontology')}>
                                <div className="icon">
                                    <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/hierarchy.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.ontology')}</div>
                            </a>
                        </li>
                        <li className={`${selectedMenu === 'details' ? 'active' : ''}`}>
                            <a className="v-tooltip" onClick={() => openEditFacility('details')} aria-label={InovxLoader.t('facility:menu.facilityDetails')}>
                                <div className="icon">
                                    <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/edit_facility.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.facilityDetails')}</div>
                            </a>
                        </li>
                        <li className={`${selectedMenu === 'integrations' ? 'active' : ''}`}>
                            <a className="v-tooltip" onClick={() => openEditFacility('integrations')} aria-label={InovxLoader.t('facility:menu.integrations')}>
                                <div className="icon">
                                    <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/integrations.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.integrations')}</div>
                            </a>
                        </li>
                        {hasManagePermission && <li className={`${selectedMenu === 'dataSources' ? 'active' : ''}`}>
                            <a className="v-tooltip" onClick={() => openDataSources()} aria-label={InovxLoader.t('facility:menu.dataSources')}>
                                <div className="icon">
                                    <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/data-sources.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.dataSources')}</div>
                            </a>
                        </li>}
                    </>) : (<>
                        {hasManagePermission && <li className={selectedMenu === 'details' ? 'active' : ''} >
                            <a className="v-tooltip" onClick={() => openEditFacility('details', 'add')} aria-label={InovxLoader.t('facility:menu.addFacility')}>
                                <div className="icon">
                                    <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/add_icon.png`} />
                                </div>
                                <div className="nav_name">
                                    {InovxLoader.t('facility:menu.addFacility')}
                                </div>
                            </a>
                        </li>}
                        {hasManagePermission && <li className={`${sidebarOpen ? '' : 'hasSubmenu'} ${selectedMenu === 'configuration' ? 'active' : ''}`}>
                            <a className="menu-item expandable d-flex j-csb" onClick={() => sidebarOpen && toggleSubmenu()}>
                                <div className="d-flex a-ic">
                                    <div className="icon">
                                        <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/menus/configurations.png`} />
                                    </div>
                                    <div className="nav_name">
                                        {InovxLoader.t('facility:menu.configurations')}
                                    </div>
                                </div>
                                <div className="icon down">
                                    {sidebarOpen && <i className={`fa fa-angle-${expandedMenu === 'edit' ? 'down' : 'up'}`} aria-hidden="true"></i>}
                                </div>
                            </a>
                            <div className={`subMenu ${expandedMenu === 'edit' ? 'show-submenu' : ''}`}>
                                <a onClick={() => openConfiguration()}>{InovxLoader.t('facility:menu.assetSchema')}</a>
                                <a onClick={() => openAssemblyLibrary()}>{InovxLoader.t('facility:menu.assemblyLibrary')}</a>
                                <a onClick={() => toggleTaxonomy()}>{InovxLoader.t('facility:menu.taxonomy')}</a>
                            </div>
                        </li>}
                    </>)}
                </ul>
            </nav>
        </div>
    );
};

export default ManageSidebar;
